import React, { useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination } from "swiper/modules";
import { X, ChevronUp, ChevronDown } from "lucide-react";
import "swiper/css";
import "swiper/css/pagination";
import { motion, AnimatePresence } from "framer-motion";

const ImageModal = ({ images, onClose, initialIndex, project }) => {
  const translation = project?.translations?.[0] || {};
  
  // Gestion individuelle des overlays pour chaque image
  const [overlayStates, setOverlayStates] = useState(
    images.map(() => false)
  );

  const toggleOverlay = (index) => {
    setOverlayStates((prev) =>
      prev.map((state, i) => (i === index ? !state : state))
    );
  };

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-80 flex items-center justify-center overflow-y-auto p-4 md:p-6">
      {/* Close Button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 md:top-6 md:right-6 text-white bg-gray-900 bg-opacity-70 p-2 rounded-full hover:bg-primary transition z-60"
        aria-label="Fermer la modale"
      >
        <X size={28} />
      </button>

      <div className="flex flex-col items-center gap-8 max-w-7xl w-full relative">
        {/* Swiper Container */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
          className="w-full flex flex-col justify-center items-center"
        >
          <Swiper
            modules={[Pagination]}
            pagination={{ clickable: true }}
            initialSlide={initialIndex}
            className="w-full"
          >
            {images.map((img, index) => (
              <SwiperSlide key={index}>
                <div className="flex justify-center items-center h-[60vh] sm:h-[70vh] md:h-[80vh] overflow-hidden relative">
                  <img
                    src={img}
                    alt={`Aperçu ${index + 1}`}
                    className="max-h-full max-w-full object-contain rounded-lg"
                  />

                  {/* Animated Overlay */}
                  <AnimatePresence>
                    {overlayStates[index] && (
                      <motion.div
                        key="overlay"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 20 }}
                        transition={{ duration: 0.3 }}
                        className="absolute inset-0 bg-black bg-opacity-60 rounded-lg p-16 md:p-22 flex flex-col justify-start text-white overflow-y-auto max-h-full"
                        style={{ backdropFilter: "blur(6px)" }}
                      >
                        <h2 className="text-xl md:text-3xl font-bold mb-3">
                          {translation.title || "Sans titre"}
                        </h2>
                        <p className="text-sm md:text-base leading-relaxed whitespace-pre-line">
                          {translation.description ||
                            "Aucune description disponible."}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Toggle Overlay Button */}
                  <div
                    className="absolute bottom-12 left-1/2 -translate-x-1/2 bg-white/20 backdrop-blur-sm text-white rounded-full px-6 py-5 shadow-lg flex items-center gap-2 cursor-pointer hover:bg-primary transition"
                    onClick={() => toggleOverlay(index)}
                    aria-label="Afficher ou cacher la description"
                  >
                    {overlayStates[index] ? (
                      <ChevronDown size={20} />
                    ) : (
                      <ChevronUp size={20} />
                    )}
                    <span className="text-sm font-medium">
                      {overlayStates[index]
                        ? "Masquer la description"
                        : "Voir la description"}
                    </span>
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>

          {/* Pagination Dots always visible and well positioned */}
          <div className="mt-4">
            <div className="swiper-pagination !static"></div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ImageModal;
